import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCashRegister } from '../../context/CashRegisterContext';
import CloseCashRegisterModal from '../location/sales/cash_register/CloseCashRegisterModal';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import PageHeader from '../../components/PageHeader/PageHeader';
import TablesService from '../../services/TablesService';
import { getLocations } from '../../services/LocationService';
import { getStoredPermissions } from '../../utils/dashboardPermissions';
import { permissionOptions } from '../../utils/Enums';
import SnackbarUtils from '../../utils/SnackbarUtils';
import FloorEditor from './FloorEditor';
import ServiceFloor from './ServiceFloor';
import CashClosedNotice from './CashClosedNotice';
import { confirmLeaveTablesEditor } from './editorGuard';
import { cashStateFrom, describeTablesError } from './tablesUtils';

const MODE = { SERVICE: 'service', EDITOR: 'editor' };

const permissionId = (label) => permissionOptions.find((permission) => permission.label === label)?.id;

const TablesPage = () => {
  const permissions = useMemo(() => getStoredPermissions(), []);
  const perms = useMemo(
    () => ({
      view: permissions.includes(permissionId('VIEW_TABLES')),
      layout: permissions.includes(permissionId('MANAGE_TABLE_LAYOUT')),
      operate: permissions.includes(permissionId('OPERATE_TABLES')),
      charge: permissions.includes(permissionId('CHARGE_TABLES')),
      void: permissions.includes(permissionId('VOID_TABLE_ITEMS')),
    }),
    [permissions]
  );

  // Cada sucursal tiene su propio salón: la sucursal viene de la ruta (/tables/:locationId), como Ventas o Inventario.
  const params = useParams();
  const locationId = Number(params.locationId);
  const [locationName, setLocationName] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // El editor se abre desde el botón Acciones de la barra superior (?mode=editor) y requiere permiso de editar el plano.
  const mode = searchParams.get('mode') === 'editor' && perms.layout ? MODE.EDITOR : MODE.SERVICE;
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [cashState, setCashState] = useState('checking');
  const { setCashOpen, closeCashRegisterModal, setCloseCashRegisterModal } = useCashRegister();

  const terminalId = localStorage.getItem('terminalId');

  useEffect(() => {
    let active = true;
    setLocationName('');
    getLocations()
      .then((data) => {
        if (!active) return;
        setLocationName((data || []).find((location) => location.id === locationId)?.name || '');
      })
      .catch(() => active && setLocationName(''));
    return () => {
      active = false;
    };
  }, [locationId]);

  const loadAreas = useCallback(
    async ({ silent = false } = {}) => {
      if (!locationId) return;
      if (!silent) {
        setAreas([]);
        setLoading(true);
      }
      try {
        const data = mode === MODE.EDITOR ? await TablesService.getAreas(locationId) : await TablesService.getFloor(locationId);
        setAreas(data || []);
        setLoadError(null);
      } catch (error) {
        if (silent) return; // un refresco en segundo plano fallido no tapa lo que ya se ve
        setAreas([]);
        setLoadError(describeTablesError(error, 'No pudimos cargar el salón'));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [locationId, mode]
  );

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  // Como en Ventas: para abrir mesas y tomar pedidos hace falta la caja abierta en la terminal de esta computadora.
  const needsCash = perms.operate;
  const checkCash = useCallback(() => {
    if (!terminalId) {
      setCashState('no_terminal');
      return undefined;
    }
    let active = true;
    setCashState('checking');
    TablesService.isCashOpen(locationId, terminalId)
      .then((response) => active && setCashState(cashStateFrom(response)))
      .catch((error) => active && setCashState(cashStateFrom(null, error)));
    return () => {
      active = false;
    };
  }, [locationId, terminalId]);

  // La barra superior muestra el estado de la caja (candado) con lo que averiguó esta pantalla.
  useEffect(() => {
    if (cashState === 'open') setCashOpen(locationId, true);
    else if (cashState === 'closed' || cashState === 'wrong_terminal') setCashOpen(locationId, false);
  }, [cashState, locationId, setCashOpen]);

  useEffect(() => {
    if (!locationId || mode !== MODE.SERVICE || !needsCash) return undefined;
    return checkCash();
  }, [locationId, mode, needsCash, checkCash]);

  if (!perms.view) {
    return <Alert severity="warning" sx={{ m: 2 }}>No tenés permiso para ver las mesas.</Alert>;
  }

  // Igual que Ventas: sin caja abierta se muestra solo el estado vacío (sin título ni controles).
  if (mode === MODE.SERVICE && needsCash && cashState === 'checking') {
    return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (mode === MODE.SERVICE && needsCash && cashState !== 'open') {
    return (
      <CashClosedNotice
        locationId={locationId}
        terminalId={terminalId}
        state={cashState === 'wrong_terminal' ? 'closed' : cashState}
        onOpened={checkCash}
        onRetry={checkCash}
      />
    );
  }

  const backToService = () => {
    if (confirmLeaveTablesEditor()) navigate(`/tables/${locationId}`);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* En el editor, "Volver" queda arriba a la derecha del encabezado. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <PageHeader
          title={locationName ? `Mesas · ${locationName}` : 'Mesas'}
          subtitle={mode === MODE.EDITOR ? 'Editor de plano' : 'Salón, cuentas abiertas y cobro por mesa'}
        />
        {mode === MODE.EDITOR && (
          <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={backToService} sx={{ flexShrink: 0, mt: 1 }}>
            Volver
          </Button>
        )}
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
      ) : !locationId ? (
        <Alert severity="info">Sucursal no válida.</Alert>
      ) : mode === MODE.EDITOR ? (
        // Recarga en segundo plano: con spinner el editor se desmontaría y perdería los cambios sin guardar.
        <FloorEditor key={locationId} locationId={locationId} areas={areas} onReload={() => loadAreas({ silent: true })} />
      ) : (
        <ServiceFloor
          key={locationId}
          locationId={locationId}
          areas={areas}
          perms={perms}
          onReload={loadAreas}
        />
      )}

      {/* El botón Cerrar caja está en la barra superior (como en Ventas): este modal lo atiende. */}
      <CloseCashRegisterModal
        open={closeCashRegisterModal}
        onClose={() => setCloseCashRegisterModal(false)}
        locationId={locationId}
        terminalId={terminalId}
        onSuccess={checkCash}
      />
    </Box>
  );
};

export default TablesPage;
